/**
 * Message Actions Component
 * Provides action buttons for messages (copy, regenerate, edit, delete)
 */

import React, { useState } from 'react';
import { Copy, RotateCw, Edit, Trash2, Check, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface MessageActionsProps {
  content: string;
  messageId: string;
  role: 'user' | 'assistant' | 'system';
  onCopy?: () => void;
  onRegenerate?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  className?: string;
}

export const MessageActions: React.FC<MessageActionsProps> = ({
  content,
  messageId,
  role,
  onCopy,
  onRegenerate,
  onEdit,
  onDelete,
  className,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      onCopy?.();
    } catch (err) {
      // console.error('Failed to copy:', err);
    }
  };

  const handleRegenerate = () => {
    onRegenerate?.();
  };

  const handleEdit = () => {
    onEdit?.();
  };

  const handleDelete = () => {
    onDelete?.();
  };

  return (
    <div className={cn('flex items-center gap-1', className)}>
      {/* Quick Copy Button */}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={handleCopy}
      >
        {copied ? (
          <>
            <Check className="h-3 w-3 mr-1" />
            <span className="text-xs">Copied</span>
          </>
        ) : (
          <>
            <Copy className="h-3 w-3 mr-1" />
            <span className="text-xs">Copy</span>
          </>
        )}
      </Button>

      {/* More Actions Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreVertical className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleCopy}>
            <Copy className="h-4 w-4 mr-2" />
            Copy message
          </DropdownMenuItem>

          {role === 'assistant' && onRegenerate && (
            <DropdownMenuItem onClick={handleRegenerate}>
              <RotateCw className="h-4 w-4 mr-2" />
              Regenerate response
            </DropdownMenuItem>
          )}

          {role === 'user' && onEdit && (
            <DropdownMenuItem onClick={handleEdit}>
              <Edit className="h-4 w-4 mr-2" />
              Edit message
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          {onDelete && (
            <DropdownMenuItem 
              onClick={handleDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete message
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
